import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import FinalCTA from "@/components/FinalCTA";
import { useConnectModal } from "@/context/ConnectModalContext";
import ScrollRail from "@/components/ScrollRail";
import { FilesRail } from "@/components/Rails";
import LottiePlayer from "@/components/LottiePlayer";
import riseAnim from "@/assets/lottie/launch-rise.json";
import filesAnim from "@/assets/lottie/files-stack.json";

const CAPABILITIES = [
  ["Technology consulting", "Independent advice on what to build, buy or retire, grounded in how the business earns and operates."],
  ["Digital infrastructure", "The foundations a business runs on, designed as one coherent system rather than a collection of tools."],
  ["Custom platforms", "Purpose-built web platforms and applications where off-the-shelf software does not fit the operating model."],
  ["Operational workflow architecture", "Mapping how work, data and decisions move through the organisation, then designing the structure that supports it."],
  ["UI/UX", "Interfaces designed around the people who use them daily, whether customers, teams or leadership."],
  ["Custom software logic", "Business rules, calculations and processes encoded in software so they run consistently without manual effort."],
  ["Workflow automation", "Repetitive handoffs and manual steps replaced with automated flows, with exceptions routed to people."],
  ["AI system integration", "AI introduced where it removes a real bottleneck, connected into existing workflows rather than bolted on."],
];

const OUTCOMES = [
  ["Streamlined acquisition funnels", "A clearer route from first interest to qualified enquiry."],
  ["Scalable digital operations", "Operations that absorb growth without adding proportionate headcount or complexity."],
  ["Integrated workflows", "Teams and systems working from the same information and the same process."],
  ["Better internal systems", "Internal tools built around real operational needs, not around what a vendor happened to ship."],
];

const POSTURE = [
  ["The business problem comes first.", "We establish what is constraining the business before any technology is proposed."],
  ["Technology is the second decision.", "The right answer is sometimes a platform, sometimes a workflow change, and sometimes nothing new at all."],
  ["We own what we deliver.", "Design, build and operation sit under the same accountable team, supervised by senior leadership."],
];

const WORK = [
  ["Revenue models", "We look at how you earn, price and retain, then design models that grow margin, not just volume."],
  ["Marketing ideas and campaigns", "Social-media-led campaigns built around a clear audience, offer and measurable goal."],
  ["Product launch support", "Positioning, launch plan and execution so a new product lands with traction, not silence."],
  ["D2C and B2B marketing", "Consultation and hands-on execution for brands selling direct to consumers or to other businesses."],
  ["Websites and apps", "Sites, apps and funnels built to lift traction and engagement, not just to exist."],
];
const WorkSection = () => (
  <section className="hp-section hp-tint" aria-labelledby="wwd-work">
    <div className="hop-container">
      <SectionHead label="What we work on" title={<span id="wwd-work">Projects across IT, tech, business help and marketing.</span>}
        lead="Strategy and execution under one team, so ideas actually get shipped." />
      <dl className="mt-10 grid md:grid-cols-2 gap-5">
        {WORK.map(([t, d]) => (<div key={t} className="hp-card p-6"><dt className="hp-h3">{t}</dt><dd className="hp-body mt-2 text-[15px]">{d}</dd></div>))}
      </dl>
    </div>
  </section>
);

const Hero = () => {
  const { openConnectModal } = useConnectModal();
  return (
    <section data-arm="hop" className="hp-hero-light hp-immersive" data-testid="wwd-hero" style={{ minHeight: "75vh" }}>
      <img 
        src="/images/wwd_tech_systems_1791101668809.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center 30%' }}
        alt="Tech systems" 
      />
      
      {/* Light gradient for text readability */}
      <div
        className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-white via-white/95 to-white/10"
        aria-hidden="true"
      />
      
      <div className="hop-container relative z-10 py-16 md:py-24 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="hp-eyebrow hp-eyebrow-bar hp-arm-accent" style={{ color: "var(--arm-hop)" }}>House of Projects · Platforms, Systems &amp; Consulting</p>
            <h1 className="hp-h1 mt-5">Systems designed around how your business actually runs.</h1>
            <p className="hp-lead mt-7" style={{ color: "#3c4768" }}>
              This is the consulting and technology arm of House of Projects. We diagnose the operational problem first, then design and deliver
              the platforms, workflows and integrations that resolve it.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button type="button" onClick={openConnectModal} className="hp-btn hp-btn-primary">
                Request an executive conversation <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
              <a href="#capabilities" className="hp-btn hp-btn-ghost">See capabilities</a>
            </div>
          </Reveal>
        </div>
        <div className="hidden lg:block lg:col-span-4">
          <LottiePlayer className="hp-lottie-hero drop-shadow-xl opacity-90 mix-blend-multiply" data={riseAnim} label="Bars rising with an upward trend arrow" />
        </div>
      </div>
      <div className="hp-rule absolute bottom-0 left-0 right-0 w-full" aria-hidden="true" />
    </section>
  );
};

const Posture = () => (
  <section className="hp-section hp-tint" aria-labelledby="wwd-posture">
    <div className="hop-container grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead label="Our position" title={<span id="wwd-posture">Not a development shop.</span>}
          lead="Most technology vendors begin with what they can build. We begin with what the business needs to change." />
      </div>
      <div className="lg:col-span-7">
        <Reveal>
          <dl className="divide-y divide-slate-200 border-y border-slate-200">
            {POSTURE.map(([t, d]) => (
              <div key={t} className="py-6 grid sm:grid-cols-[17rem_1fr] gap-1 sm:gap-6">
                <dt className="font-display text-xl font-semibold text-hop-blue">{t}</dt>
                <dd className="hp-body text-[15px]">{d}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </div>
  </section>
);

const Capabilities = () => (
  <section id="capabilities" className="hp-section relative overflow-hidden bg-[#f7f7f5]" aria-labelledby="wwd-caps">
    {/* Background Image with strong readable gradient fade */}
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
      <img
        src="/images/wwd_tech_systems_1791101668809.jpg"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#f7f7f5] via-[#f7f7f5]/90 to-[#f7f7f5]/10" />
    </div>

    <div className="hop-container relative" style={{ zIndex: 10 }}>
      <SectionHead label="Capabilities" title={<span id="wwd-caps">What we design and deliver.</span>}
        lead="Engagements usually combine several of these, scoped to the problem identified during diagnosis." />
      <div className="mt-14">
        <ul data-testid="wwd-capabilities" className="grid md:grid-cols-2 gap-8">
          {CAPABILITIES.map(([t, d], i) => (
            <li key={t} data-arm="hop" className="hp-beat relative bg-white/40 backdrop-blur-md p-6 rounded-xl border border-slate-200/60 shadow-sm">
              <span className="hp-beat-n">0{i + 1}</span>
              <h3 className="hp-h3 mt-1">{t}</h3>
              <p className="hp-body mt-2 text-[15px] max-w-md">{d}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Outcomes = () => (
  <section className="hp-section hp-ink" aria-labelledby="wwd-out">
    <div className="hop-container">
      <SectionHead dark label="Outcomes" title={<span id="wwd-out">What changes for the business.</span>} />
      <Reveal>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/15 border border-white/15 rounded-md overflow-hidden">
          {OUTCOMES.map(([t, d]) => (
            <div key={t} className="bg-[var(--hop-blue)] p-7">
              <h3 className="font-display text-2xl font-semibold text-white">{t}</h3>
              <p className="mt-3 text-[15px] text-slate-300 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Delivered = () => (
  <section className="hp-section" aria-labelledby="wwd-del">
    <div className="hop-container grid lg:grid-cols-12 gap-14 items-start">
      <div className="lg:col-span-6">
        <SectionHead label="Delivered work" title={<span id="wwd-del">10+ custom web architectures designed and delivered.</span>}
          lead="Each began as an operational problem, not a feature list. Named examples are shared in conversation where client confidentiality allows." />
      </div>
      <Reveal delay={100} className="lg:col-span-6">
        <LottiePlayer className="w-full max-w-[360px] aspect-square mb-6" data={filesAnim} label="Project files being prepared and checked off" />
        <p className="hp-body">
          The same approach applies whether the engagement is a single platform or a connected set of systems: define the constraint,
          design the structure, build it, then measure and refine it as the business changes.
        </p>
      </Reveal>
    </div>
  </section>
);

export default function WhatWeDo() {
  return (
    <>
      <Seo title="What We Do: Platforms, Systems & Consulting"
        description="House of Projects provides technology consulting, digital infrastructure, custom platforms, workflow architecture, UI/UX, automation and AI system integration, starting from the business problem."
        path="/what-we-do" />
      <Hero /><Posture /><Capabilities /><Outcomes /><Delivered />
      <WorkSection />
      <FinalCTA />
    </>
  );
}
